import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile,
    onAuthStateChanged,
    User as FirebaseUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase.config';
import { User, UserPreferences, ConversionHistoryItem } from '../types/userTypes';
import EmailService from './emailService';

class UserAuthService {
    private static instance: UserAuthService;
    private currentUser: User | null = null;
    private authStateListeners: Array<(authState: any) => void> = [];
    private isInitialized: boolean = false;

    static getInstance(): UserAuthService {
        console.log('🔧 UserAuthService: getInstance called');
        if (!UserAuthService.instance) {
            console.log('🔧 UserAuthService: Creating new instance');
            UserAuthService.instance = new UserAuthService();
            console.log('🔧 UserAuthService: Instance created:', UserAuthService.instance);
        } else {
            console.log('🔧 UserAuthService: Returning existing instance');
        }
        return UserAuthService.instance;
    }

    constructor() {
        console.log('🔧 UserAuthService: Constructor called');
        try {
            this.initializeAuth();
            console.log('🔧 UserAuthService: Constructor completed successfully');
        } catch (error) {
            console.error('🔧 UserAuthService: Constructor failed:', error);
            throw error;
        }
    }

    private initializeAuth() {
        onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser) {
                this.currentUser = await this.loadUserData(firebaseUser);
            } else {
                this.currentUser = null;
            }

            this.notifyAuthStateListeners();
        });
    }

    // Registration
    async register(email: string, password: string, displayName?: string): Promise<User> {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;

            // Update display name if provided
            if (displayName) {
                await updateProfile(firebaseUser, { displayName });
            }

            // Create user document in Firestore
            const userData: User = {
                uid: firebaseUser.uid,
                email: firebaseUser.email!,
                displayName: firebaseUser.displayName,
                photoURL: firebaseUser.photoURL,
                emailVerified: firebaseUser.emailVerified,
                createdAt: new Date(),
                lastLogin: new Date(),
                isActive: true,
                preferences: this.getDefaultPreferences()
            };

            await setDoc(doc(db, 'users', firebaseUser.uid), {
                ...userData,
                createdAt: serverTimestamp(),
                lastLogin: serverTimestamp()
            });

            this.currentUser = userData;
            this.notifyAuthStateListeners();
            return userData;

        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    }

    // Login
    async login(email: string, password: string): Promise<User> {
        console.log('🔧 UserAuthService: login method called with:', email);
        try {
            console.log('🔧 UserAuthService: Calling signInWithEmailAndPassword...');
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            console.log('🔧 UserAuthService: Firebase auth successful');
            const firebaseUser = userCredential.user;

            // Load user data
            console.log('🔧 UserAuthService: Loading user data...');
            const userData = await this.loadUserData(firebaseUser);

            // Update last login
            console.log('🔧 UserAuthService: Updating last login...');
            await updateDoc(doc(db, 'users', firebaseUser.uid), {
                lastLogin: serverTimestamp()
            });

            this.currentUser = userData;
            this.notifyAuthStateListeners();
            console.log('🔧 UserAuthService: Login completed successfully');
            return userData;

        } catch (error) {
            console.error('🔧 UserAuthService: Login error:', error);
            throw error;
        }
    }

    // Logout
    async logout(): Promise<void> {
        try {
            await signOut(auth);
            this.currentUser = null;
            this.notifyAuthStateListeners();
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }

    // Password Reset
    async resetPassword(email: string): Promise<void> {
        try {
            // Use Firebase's built-in password reset with custom action code settings
            const actionCodeSettings = {
                url: `${window.location.origin}/auth?mode=resetPassword`,
                handleCodeInApp: false,
            };

            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            console.log('✅ Password reset email sent via Firebase');
            
            // Optional: Also send via custom email service for better deliverability
            try {
                const emailService = EmailService.getInstance();
                // Note: This would require getting the reset link from Firebase
                // For now, we'll rely on Firebase's built-in system
                console.log('📧 Custom email service available for future use');
            } catch (emailError) {
                console.warn('⚠️ Custom email service not configured, using Firebase only');
            }
        } catch (error: any) {
            console.error('❌ Password reset error:', error);
            
            // Provide more specific error messages
            if (error.code === 'auth/user-not-found') {
                throw new Error('No account found with this email address');
            } else if (error.code === 'auth/invalid-email') {
                throw new Error('Please enter a valid email address');
            } else if (error.code === 'auth/too-many-requests') {
                throw new Error('Too many reset attempts. Please try again later');
            } else {
                throw new Error('Failed to send reset email. Please try again');
            }
        }
    }

    // Load user data from Firestore
    private async loadUserData(firebaseUser: FirebaseUser): Promise<User> {
        try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (userDoc.exists()) {
                const data = userDoc.data();
                return {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email!,
                    displayName: firebaseUser.displayName,
                    photoURL: firebaseUser.photoURL,
                    emailVerified: firebaseUser.emailVerified,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    lastLogin: data.lastLogin?.toDate() || new Date(),
                    isActive: data.isActive !== false,
                    preferences: data.preferences || this.getDefaultPreferences()
                };
            } else {
                // Create user document if it doesn't exist
                const userData: User = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email!,
                    displayName: firebaseUser.displayName,
                    photoURL: firebaseUser.photoURL,
                    emailVerified: firebaseUser.emailVerified,
                    createdAt: new Date(),
                    lastLogin: new Date(),
                    isActive: true,
                    preferences: this.getDefaultPreferences()
                };

                await setDoc(doc(db, 'users', firebaseUser.uid), {
                    ...userData,
                    createdAt: serverTimestamp(),
                    lastLogin: serverTimestamp()
                });

                return userData;
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            throw error;
        }
    }

    // Get default user preferences
    private getDefaultPreferences(): UserPreferences {
        return {
            language: 'en',
            theme: 'auto',
            defaultFromCurrency: 'USD',
            defaultToCurrency: 'EUR',
            favoriteCurrencies: ['USD', 'EUR', 'GBP', 'JPY', 'CAD'],
            calculatorSettings: {
                decimalPlaces: 2,
                autoRefresh: true,
                refreshInterval: 5,
                showReverseRates: true,
                showHistoricalRates: false
            },
            watchlist: ['USD/EUR', 'USD/GBP', 'USD/JPY'],
            conversionHistory: []
        };
    }

    // Update user preferences
    async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
        if (!this.currentUser) throw new Error('No authenticated user');

        try {
            const updatedPreferences = { ...this.currentUser.preferences, ...preferences };

            await updateDoc(doc(db, 'users', this.currentUser.uid), {
                preferences: updatedPreferences
            });

            this.currentUser.preferences = updatedPreferences;
            this.notifyAuthStateListeners();
        } catch (error) {
            console.error('Error updating preferences:', error);
            throw error;
        }
    }

    // Add conversion to history
    async addConversionToHistory(conversion: Omit<ConversionHistoryItem, 'id' | 'timestamp'>): Promise<void> {
        if (!this.currentUser) return;

        try {
            const newConversion: ConversionHistoryItem = {
                ...conversion,
                id: Date.now().toString(),
                timestamp: new Date()
            };

            const updatedHistory = [...this.currentUser.preferences.conversionHistory, newConversion];

            // Keep only last 100 conversions
            if (updatedHistory.length > 100) {
                updatedHistory.splice(0, updatedHistory.length - 100);
            }

            await this.updatePreferences({
                conversionHistory: updatedHistory
            });
        } catch (error) {
            console.error('Error adding conversion to history:', error);
        }
    }

    // Auth state management
    onAuthStateChanged(listener: (user: User | null) => void): () => void {
        const wrappedListener = (authState: any) => {
            listener(authState.user);
        };
        return this.addAuthStateListener(wrappedListener);
    }

    addAuthStateListener(listener: (authState: any) => void): () => void {
        this.authStateListeners.push(listener);

        // Return unsubscribe function
        return () => {
            const index = this.authStateListeners.indexOf(listener);
            if (index > -1) {
                this.authStateListeners.splice(index, 1);
            }
        };
    }

    private notifyAuthStateListeners() {
        const authState = {
            user: this.currentUser,
            isLoading: false,
            error: null,
            isAuthenticated: !!this.currentUser
        };

        this.authStateListeners.forEach(listener => listener(authState));
    }

    // Getters
    getCurrentUser(): User | null {
        return this.currentUser;
    }

    isAuthenticated(): boolean {
        return !!this.currentUser;
    }
}

export default UserAuthService;
